import React, { useState, useEffect } from 'react';
import { Database, Trash2, X, ShieldCheck, LogOut, Loader2, AlertCircle, Server, Palette, Globe, Cpu, Tag } from 'lucide-react';
import { db, auth, googleProvider, signInWithPopup } from '../services/firebase.ts';
import { collection, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';

interface SystemLead {
  id: string;
  name: string;
  email: string;
  business: string;
  selections: string[];
  oneTime: number;
  monthly: number;
  date: string;
  aiRoute?: 'ours' | 'own';
  brandRoute?: 'own' | 'pro';
  hostingRoute?: 'ours' | 'own';
  domainRoute?: 'own' | 'setup';
  promoUsed?: string;
}

interface NewsletterSub {
  id: string;
  email: string;
  date: string;
}

interface ConsultationLead {
  id: string;
  email: string;
  mobile: string;
  date: string;
}

// STRICT ACCESS CONTROL
const AUTHORIZED_EMAILS = ["arthoven@overpowered.co", "hilda@overpowered.co"];

const AdminPortal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<'systems' | 'newsletter' | 'consultation'>('systems');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [systemLeads, setSystemLeads] = useState<SystemLead[]>([]);
  const [newsletterSubs, setNewsletterSubs] = useState<NewsletterSub[]>([]);
  const [consultationLeads, setConsultationLeads] = useState<ConsultationLead[]>([]);

  const fetchAllData = async () => {
    if (!db) {
      setError("Database is offline. Check API Keys.");
      return;
    }

    setIsLoading(true);
    try {
      // FIX: Read from the correct sub-collections where data is actually saved
      const systemQuery = query(collection(db, 'apps/tiny-systems/system_leads'), orderBy('date', 'desc'));
      const systemSnapshot = await getDocs(systemQuery);
      setSystemLeads(systemSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SystemLead)));

      const trainingQuery = query(collection(db, 'apps/tiny-systems/training_leads'), orderBy('date', 'desc'));
      const trainingSnapshot = await getDocs(trainingQuery);
      setConsultationLeads(trainingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ConsultationLead)));

      // Note: App.tsx writes to 'newsletter', not 'newsletter_subs'
      const newsletterQuery = query(collection(db, 'apps/tiny-systems/newsletter'), orderBy('date', 'desc'));
      const newsletterSnapshot = await getDocs(newsletterQuery);
      setNewsletterSubs(newsletterSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as NewsletterSub)));
    } catch (err) {
      console.error("Fetch Data Error:", err);
      setError("Failed to fetch records from Firestore.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const handleGoogleLogin = async () => {
    setError(null);
    setIsLoggingIn(true);

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      if (user.email && AUTHORIZED_EMAILS.includes(user.email)) {
        setIsAuthenticated(true);
      } else {
        await auth.signOut();
        setError(`Access Denied: ${user.email} is not authorized.`);
      }
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/popup-closed-by-user') {
         setError("Login cancelled.");
      } else {
         setError("Google Sign-In failed.");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleDelete = async (coll: string, id: string) => {
    if (!db) return;
    if (!confirm("Are you sure you want to delete this record forever?")) return;
    try {
      await deleteDoc(doc(db, coll, id));
      fetchAllData(); 
    } catch (err) {
      console.error(err);
      alert("Delete failed. Check Firebase permissions.");
    }
  };

  const logout = async () => {
    await auth.signOut();
    setIsAuthenticated(false);
    onClose();
  };

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-[#1F2933]/95 z-[100] flex items-center justify-center p-6 backdrop-blur-md">
        <div className="bg-[#F4F1EC] p-10 md:p-14 rounded-[3rem] w-full max-w-md shadow-2xl border border-white/20 animate-in zoom-in-95 duration-300">
          <div className="flex justify-center mb-8">
            <div className="bg-[#7FAF9A] p-5 rounded-[2rem] text-white shadow-lg">
              <ShieldCheck size={48} />
            </div>
          </div>
          <h2 className="text-3xl font-black text-center mb-8 tracking-tight text-[#1F2933]">Portal Entry</h2>
          
          <div className="space-y-4">
            {error && (
              <div className="flex items-center gap-3 p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold border border-red-100 animate-in fade-in slide-in-from-top-2">
                <AlertCircle size={16} className="shrink-0" />
                {error}
              </div>
            )}

            <button 
              onClick={handleGoogleLogin}
              disabled={isLoggingIn}
              className="w-full bg-white text-[#1F2933] py-4 rounded-2xl font-black text-sm hover:bg-gray-50 transition-all shadow-md border border-gray-200 flex items-center justify-center gap-3 mt-4"
            >
              {isLoggingIn ? <Loader2 size={18} className="animate-spin" /> : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </>
              )}
            </button>
            
            <div className="text-center pt-6">
              <button 
                type="button"
                onClick={onClose} 
                className="text-[#1F2933]/30 text-[10px] font-black uppercase tracking-[0.3em] hover:text-[#1F2933] transition-colors"
              >
                Return to Site
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#F4F1EC] z-[100] overflow-y-auto animate-in fade-in duration-500">
      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-20">
        <div className="flex justify-between items-center mb-16 gap-8">
          <div className="flex items-center gap-6">
            <div className="bg-[#1F2933] p-4 rounded-[1.5rem] text-white shadow-xl">
              <Database size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-black text-[#1F2933]">Studio Command</h1>
              <p className="text-[#1F2933]/50 font-bold text-sm uppercase tracking-widest mt-1">Live Firestore Feed</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={logout} className="px-6 py-3 bg-white rounded-2xl text-sm font-bold text-red-500 border border-red-100 hover:bg-red-50 transition-colors flex items-center gap-2">
              <LogOut size={18} />
              Logout
            </button>
            <button onClick={onClose} className="p-3 bg-[#1F2933] text-white rounded-full hover:scale-110 transition-transform"><X size={24} /></button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-10">
          <button onClick={() => setActiveTab('systems')} className={`px-8 py-4 rounded-2xl font-black text-sm border-2 transition-all ${activeTab === 'systems' ? 'bg-[#7FAF9A] text-white border-[#7FAF9A] shadow-lg shadow-green-900/10' : 'bg-white text-[#1F2933]/40 border-transparent hover:bg-gray-50'}`}>Builds ({systemLeads.length})</button>
          <button onClick={() => setActiveTab('consultation')} className={`px-8 py-4 rounded-2xl font-black text-sm border-2 transition-all ${activeTab === 'consultation' ? 'bg-[#7FAF9A] text-white border-[#7FAF9A] shadow-lg shadow-green-900/10' : 'bg-white text-[#1F2933]/40 border-transparent hover:bg-gray-50'}`}>Training ({consultationLeads.length})</button>
          <button onClick={() => setActiveTab('newsletter')} className={`px-8 py-4 rounded-2xl font-black text-sm border-2 transition-all ${activeTab === 'newsletter' ? 'bg-[#7FAF9A] text-white border-[#7FAF9A] shadow-lg shadow-green-900/10' : 'bg-white text-[#1F2933]/40 border-transparent hover:bg-gray-50'}`}>Newsletter ({newsletterSubs.length})</button>
        </div>

        <div className="bg-white rounded-[3rem] shadow-2xl border border-[#1F2933]/5 overflow-hidden min-h-[400px]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-96 text-[#1F2933]/20">
              <Loader2 size={48} className="animate-spin mb-4 text-[#7FAF9A]" />
              <p className="font-bold uppercase tracking-[0.2em] text-[10px]">Syncing with Firestore...</p>
            </div>
          ) : !db ? (
             <div className="flex flex-col items-center justify-center h-96 text-[#1F2933]/40">
              <AlertCircle size={48} className="mb-4 text-red-400" />
              <p className="font-bold uppercase tracking-[0.2em] text-[10px] text-red-500">Database Offline</p>
              <p className="text-xs mt-2">API Keys missing in environment.</p>
            </div>
          ) : activeTab === 'systems' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-[#1F2933]/5 text-[11px] font-black uppercase tracking-[0.2em] text-[#1F2933]/40">
                  <tr>
                    <th className="px-10 py-8">Founder</th>
                    <th className="px-10 py-8">Configuration & Features</th>
                    <th className="px-10 py-8">Quote</th>
                    <th className="px-10 py-8 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {systemLeads.map(lead => (
                    <tr key={lead.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-10 py-8 align-top w-1/4">
                        <div className="space-y-1">
                          <p className="font-extrabold text-lg text-[#1F2933]">{lead.name}</p>
                          <p className="text-sm font-medium text-[#7FAF9A]">{lead.email}</p>
                          <div className="pt-2">
                             <span className="text-[10px] font-bold bg-[#E8C547]/10 text-[#1F2933]/70 px-2 py-1 rounded inline-block uppercase tracking-wider">
                               {lead.business}
                             </span>
                          </div>
                          <p className="text-[10px] font-bold text-[#1F2933]/20 uppercase tracking-widest pt-4">
                            {new Date(lead.date).toLocaleDateString()}
                          </p>
                        </div>
                      </td>
                      <td className="px-10 py-8 align-top">
                        {/* Selected Features */}
                        <div className="mb-6">
                           <p className="text-[9px] font-black text-[#1F2933]/30 uppercase tracking-[0.2em] mb-2">Modules</p>
                           <div className="flex flex-wrap gap-1.5">
                            {lead.selections.map(s => (
                              <span key={s} className="text-[10px] font-bold border border-[#1F2933]/10 px-3 py-1.5 rounded-lg bg-white text-[#1F2933]">
                                {s === 'base' && 'Web App Core'}
                                {s === 'chatbot' && 'AI Concierge'}
                                {s === 'tracking' && 'Dashboard'}
                                {s === 'whatsapp' && 'WA Bridge'}
                                {s === 'telegram' && 'TG Sync'}
                                {!['base','chatbot','tracking','whatsapp','telegram'].includes(s) && s}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Config Grid */}
                         <div className="grid grid-cols-2 gap-4">
                           <div className="bg-[#F4F1EC]/50 p-3 rounded-xl border border-[#1F2933]/5">
                             <div className="flex items-center gap-2 mb-1">
                               <Cpu size={12} className="text-[#7FAF9A]" />
                               <span className="text-[9px] font-black uppercase tracking-wider text-[#1F2933]/40">AI Brain</span>
                             </div>
                             <p className="text-xs font-bold text-[#1F2933]">
                               {lead.aiRoute === 'ours' ? 'Managed (Studio)' : 'Client Keys (BYO)'}
                             </p>
                           </div>
                           
                           <div className="bg-[#F4F1EC]/50 p-3 rounded-xl border border-[#1F2933]/5">
                             <div className="flex items-center gap-2 mb-1">
                               <Server size={12} className="text-[#7FAF9A]" />
                               <span className="text-[9px] font-black uppercase tracking-wider text-[#1F2933]/40">Hosting</span>
                             </div>
                             <p className="text-xs font-bold text-[#1F2933]">
                               {lead.hostingRoute === 'ours' ? 'Tiny Cloud' : 'Self-Hosted'}
                             </p>
                           </div>

                           <div className="bg-[#F4F1EC]/50 p-3 rounded-xl border border-[#1F2933]/5">
                             <div className="flex items-center gap-2 mb-1">
                               <Palette size={12} className="text-[#7FAF9A]" />
                               <span className="text-[9px] font-black uppercase tracking-wider text-[#1F2933]/40">Branding</span>
                             </div>
                             <p className="text-xs font-bold text-[#1F2933]">
                               {lead.brandRoute === 'pro' ? 'Full Design Sesh' : 'Existing Assets'}
                             </p>
                           </div>

                           <div className="bg-[#F4F1EC]/50 p-3 rounded-xl border border-[#1F2933]/5">
                             <div className="flex items-center gap-2 mb-1">
                               <Globe size={12} className="text-[#7FAF9A]" />
                               <span className="text-[9px] font-black uppercase tracking-wider text-[#1F2933]/40">Domain</span>
                             </div>
                             <p className="text-xs font-bold text-[#1F2933]">
                               {lead.domainRoute === 'setup' ? 'Setup Required' : 'Existing'}
                             </p>
                           </div>
                         </div>
                      </td>
                      <td className="px-10 py-8 align-top">
                        <div className="flex flex-col gap-1">
                           <span className="font-black text-2xl text-[#1F2933]">SGD {lead.oneTime}</span>
                           <span className="text-[10px] font-bold uppercase tracking-wider text-[#1F2933]/30">One-time</span>
                           
                           {lead.monthly > 0 && (
                             <div className="mt-2 inline-flex flex-col">
                               <span className="font-bold text-[#7FAF9A]">SGD {lead.monthly}/mo</span>
                               <span className="text-[9px] font-bold uppercase tracking-wider text-[#7FAF9A]/50">Recurring</span>
                             </div>
                           )}

                           {lead.promoUsed && lead.promoUsed !== 'NONE' && (
                             <div className="mt-4 flex items-center gap-2 text-[#E8C547]">
                               <Tag size={12} />
                               <span className="text-[10px] font-black uppercase tracking-wider">{lead.promoUsed}</span>
                             </div>
                           )}
                        </div>
                      </td>
                      <td className="px-10 py-8 align-top text-right">
                        <button onClick={() => handleDelete('apps/tiny-systems/system_leads', lead.id)} className="text-red-200 hover:text-red-500 hover:bg-red-50 transition-all p-3 rounded-xl">
                          <Trash2 size={20} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {systemLeads.length === 0 && (
                    <tr><td colSpan={4} className="px-10 py-20 text-center text-[#1F2933]/20 font-bold italic">No build requests yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : activeTab === 'consultation' ? (
             <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-[#1F2933]/5 text-[11px] font-black uppercase tracking-[0.2em] text-[#1F2933]/40">
                  <tr><th className="px-10 py-8">Contact</th><th className="px-10 py-8">Date</th><th className="px-10 py-8 text-right">Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {consultationLeads.map(lead => (
                    <tr key={lead.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-10 py-8 font-extrabold text-lg">{lead.email} <br/><span className="text-sm text-[#7FAF9A] font-bold">{lead.mobile}</span></td>
                      <td className="px-10 py-8 opacity-50 font-bold">{new Date(lead.date).toLocaleDateString()}</td>
                      <td className="px-10 py-8 text-right"><button onClick={() => handleDelete('apps/tiny-systems/training_leads', lead.id)} className="text-red-200 hover:text-red-500 transition-colors p-2"><Trash2 size={20} /></button></td>
                    </tr>
                  ))}
                  {consultationLeads.length === 0 && (
                    <tr><td colSpan={3} className="px-10 py-20 text-center text-[#1F2933]/20 font-bold italic">No training inquiries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-[#1F2933]/5 text-[11px] font-black uppercase tracking-[0.2em] text-[#1F2933]/40">
                  <tr><th className="px-10 py-8">Email</th><th className="px-10 py-8">Subscribed</th><th className="px-10 py-8 text-right">Actions</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {newsletterSubs.map(sub => (
                    <tr key={sub.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-10 py-8 font-extrabold text-lg">{sub.email}</td>
                      <td className="px-10 py-8 opacity-50 font-bold">{new Date(sub.date).toLocaleDateString()}</td>
                      <td className="px-10 py-8 text-right"><button onClick={() => handleDelete('apps/tiny-systems/newsletter', sub.id)} className="text-red-200 hover:text-red-500 transition-colors p-2"><Trash2 size={20} /></button></td>
                    </tr>
                  ))}
                  {newsletterSubs.length === 0 && (
                    <tr><td colSpan={3} className="px-10 py-20 text-center text-[#1F2933]/20 font-bold italic">No newsletter subscribers yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPortal;
